import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import { BellIcon } from "@heroicons/react/24/outline";

// Notification Dropdown Component
const NotificationDropdown = () => {
  const darkMode = useSelector(selectDarkMode);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      title: "New Appointment Scheduled",
      message: "John Doe has scheduled an appointment for COVID-19 vaccine",
      time: "5 mins ago",
      unread: true,
    },
    {
      id: 2,
      title: "Stock Alert",
      message: "Pfizer vaccine stock is running low at Center A",
      time: "1 hour ago",
      unread: true,
    },
    {
      id: 3,
      title: "New Staff Added",
      message: "Dr. Sarah Johnson has been added to the medical staff",
      time: "2 hours ago",
      unread: true,
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-full relative transition-colors ${
          darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
        }`}
      >
        <BellIcon className={`h-6 w-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
        {notifications.filter((n) => n.unread).length > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {notifications.filter((n) => n.unread).length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={`absolute right-0 mt-2 w-80 rounded-lg shadow-lg border z-50 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Notifications</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 border-b cursor-pointer transition-colors ${
                    darkMode
                      ? `border-gray-700 hover:bg-gray-700 ${notification.unread ? 'bg-blue-900/20' : ''}`
                      : `border-gray-100 hover:bg-gray-50 ${notification.unread ? 'bg-blue-50' : ''}`
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {notification.title}
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {notification.message}
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {notification.time}
                      </p>
                    </div>
                    {notification.unread && (
                      <div className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <BellIcon className={`h-12 w-12 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p>No notifications</p>
              </div>
            )}
          </div>
          <div className={`p-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium">
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
